'use client';

import { useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';
import { fbs } from 'fbtee';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log the error to Sentry
    Sentry.captureException(error, {
      fingerprint: ['app-error-boundary', error.digest ?? 'unknown'],
      tags: {
        errorBoundary: 'app-root',
        digest: error.digest,
      },
    });
  }, [error]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4 text-center">
        <h1 className="text-2xl font-bold text-red-600">
          {fbs('Something went wrong!', 'Error page heading')}
        </h1>
        <p className="text-gray-600">
          {fbs(
            "An error occurred while processing your request. The error has been logged and we'll look into it.",
            'Error page description'
          )}
        </p>
        <div className="space-y-2">
          <button
            onClick={reset}
            className="w-full rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 transition-colors"
          >
            {fbs('Try again', 'Button to retry after error')}
          </button>
          <button
            onClick={() => (window.location.href = '/')}
            className="w-full rounded-lg bg-gray-200 px-4 py-2 text-gray-800 hover:bg-gray-300 transition-colors"
          >
            {fbs('Go home', 'Button to navigate to home page after error')}
          </button>
        </div>
        {process.env.NODE_ENV === 'development' && (
          <details className="mt-4 text-left">
            <summary className="cursor-pointer text-sm text-gray-500">
              Error details (dev only)
            </summary>
            <pre className="mt-2 whitespace-pre-wrap text-xs text-red-600 bg-red-50 p-2 rounded">
              {error.message}
              {error.stack}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}
